import React, { useState } from 'react';
import { Form, Input, message, Button } from 'antd';
import { AlertCircle, Info, Shield, UserPlus } from 'lucide-react';
import { updateExploitation } from '../../Utils/exploitations/exploitations';

const ExpPassword = ({ onClose, exploitationId }) => {
  const [form] = Form.useForm();
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [showPasswordRequired, setShowPasswordRequired] = useState(false);

  const onFinish = async (values) => {
    if (values.newPassword !== values.confirmPassword) {
      message.error("Les mots de passe ne correspondent pas");
      return;
    }

    setConfirmLoading(true);
    setShowPasswordRequired(false);

    try {
      const updateData = {};
      if (values.newPassword) {
        updateData.password = values.newPassword;
      }
      if (values.email) {
        updateData.email = values.email;
      }

      await updateExploitation(exploitationId, updateData);
      message.success('Informations mises à jour avec succès');
      onClose();
    } catch (error) {
      if (error.response?.status === 404) {
        message.error('Gestionnaire non trouvé');
      } else if (error.response?.status === 433) {
        setShowPasswordRequired(true);
        form.setFields([
          {
            name: 'newPassword',
            errors: ['Un mot de passe est requis pour créer un nouveau compte']
          }
        ]);
      } else {
        message.error('Erreur lors de la mise à jour des informations');
      }
      console.error('Error updating information:', error);
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <Form form={form} onFinish={onFinish} layout="vertical">
      <h1 className="text-3xl font-bold mb-4">Modifier les informations</h1>
      
      <div className="space-y-6">
        {/* Email Section */}
        <div className="space-y-3">
          <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-md border border-blue-200">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-2">
              <p className="text-blue-700 text-sm">
                Si vous entrez une adresse email déjà associée à un compte existant, 
                ce compte sera automatiquement assigné comme gestionnaire de cette exploitation.
              </p>
              <p className="text-blue-700 text-sm">
                Si l'email n'est pas associé à un compte existant, vous devrez définir un mot de passe 
                pour créer un nouveau compte.
              </p>
            </div>
          </div>

          {/* if the admin just want to modify the email he ahould do so in the users page 
              if the admin enters a new email that is not associated with any account, a new account will be created
           */}
          <div className='flex items-start gap-2 p-3 bg-blue-50 rounded-md border border-blue-200'>
            <Shield className='w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5' />
            <div className='space-y-2'>
              <p className='text-blue-700 text-sm font-medium'>
                Modification de l'adresse e-mail
              </p>
              <p className='text-blue-700 text-sm'>
                Si vous souhaitez modifier l'adresse e-mail du gestionnaire, veuillez le faire sur la page des utilisateurs.
              </p>
            </div>
          </div>


          <Form.Item
            name="email"
            label="Email"
            rules={[
              {
                type: 'email',
                message: 'Veuillez entrer une adresse email valide!',
              },
            ]}
          >
            <Input autoComplete="email" placeholder="Entrez votre adresse email" />
          </Form.Item>
        </div>

        {/* Password Required Alert */}
        {showPasswordRequired && (
          <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-md border border-blue-200">
            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="space-y-2">
              <p className="text-blue-700 text-sm font-medium">
                Création de nouveau compte requise
              </p>
              <p className="text-blue-700 text-sm">
                L'email saisi n'est pas associé à un compte existant. Pour continuer, 
                veuillez définir un mot de passe afin de créer un nouveau compte qui sera 
                gestionnaire de cette exploitation.
              </p>
            </div>
          </div>
        )}

        {/* Password Section */}
        <div className="border-t pt-6">
          <h2 className="text-xl font-semibold mb-4">Changer le mot de passe</h2>

          <Form.Item
            name="newPassword"
            label="Nouveau mot de passe"
            rules={[
              { min: 8, message: 'Le mot de passe doit contenir au moins 8 caractères' },
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirmer le mot de passe"
            dependencies={['newPassword']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value && !getFieldValue('newPassword')) {
                    return Promise.resolve();
                  }
                  if (!value && getFieldValue('newPassword')) {
                    return Promise.reject(new Error('Veuillez confirmer votre mot de passe!'));
                  }
                  if (getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Les deux mots de passe ne correspondent pas!'));
                },
              }),
            ]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </div>

        {/* Optional Fields Note */}
        <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-md border border-blue-200">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-blue-700 text-sm">
            Les champs sont optionnels sauf si vous créez un nouveau compte. Dans ce cas, 
            un mot de passe est requis.
          </p>
        </div>

        <Form.Item className="mt-8">
          <Button 
            type="primary" 
            htmlType="submit" 
            loading={confirmLoading}
            className="w-full"
          >
            Mettre à jour les informations
          </Button>
        </Form.Item>
      </div>
    </Form>
  );
};

export default ExpPassword;